<div class="sku-edit-container">
  <p-toast></p-toast>
  <p-confirmDialog
    key="skuConfirm"
    [styleClass]="
      'max-w-md w-[28rem] rounded-xl shadow-2xl border-0 overflow-hidden'
    "
    [maskStyleClass]="'backdrop-blur-sm bg-black/40'"
    appendTo="body"
  >
    <ng-template pTemplate="message" let-message>
      <div class="px-3 pl-0 py-2">
        <p class="text-gray-800 dark:text-gray-200 leading-6">
          {{ message?.message | translate }}
        </p>
      </div>
    </ng-template>
  </p-confirmDialog>
  <!-- Sticky Header -->
  <div class="sticky-header" [class.scrolled]="isScrolled()">
    <div class="flex justify-between items-center mb-5">
      <div class="flex items-center">
        <h1 class="text-2xl font-semibold text-gray-800">
          {{ "sku_management.edit.title" | translate }}
        </h1>
      </div>
      <div class="flex gap-3">
        <app-button
          [label]="'sku_management.edit.cancel' | translate"
          type="secondary"
          (emitOnClick)="onCancel()"
          [outlined]="true"
        ></app-button>
        <app-button
          [label]="saveButtonLabelKey() | translate"
          type="info"
          (emitOnClick)="onSave()"
          [disabled]="!editForm.valid || loading()"
          [loading]="loading()"
        ></app-button>
      </div>
    </div>
  </div>

  <!-- Content -->
  <div class="sku-edit-content">
    @if (skuItem()) {
      @if (skuItem(); as item) {
        <form [formGroup]="editForm" class="space-y-6">
          <!-- Basic Information Section -->
          <p-card styleClass="border-0 overflow-hidden">
            <ng-template pTemplate="header">
              <div class="flex items-center">
                <h2 class="text-lg font-medium text-gray-800">
                  {{
                    "sku_management.detail.sections.basic_information"
                      | translate
                  }}
                </h2>
              </div>
            </ng-template>
            <ng-template pTemplate="content">
              <div class="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div class="space-y-2">
                  <span class="text-sm font-medium text-gray-600"
                    >{{ "sku_management.detail.fields.sku_name" | translate }}
                    <span class="text-red-500">*</span></span
                  >
                  <p class="text-gray-800">{{ item.skuName }}</p>
                </div>

                <div class="space-y-2">
                  <label
                    for="sku-sku-code"
                    class="text-sm font-medium text-gray-600"
                    >{{ "sku_management.detail.fields.sku_code" | translate }}
                    <span class="text-red-500">*</span>
                    <span
                      class="material-symbols-outlined ml-1 text-gray-400 hover:text-blue-500 cursor-help text-xs"
                      pTooltip="Only letters, numbers, hyphens, and underscores allowed (max 32 characters)"
                      tooltipPosition="top"
                      >info</span
                    >
                  </label>
                  @if (isSkuCodeEditable()) {
                    <input
                      pInputText
                      id="sku-sku-code"
                      [formControlName]="'skuCode'"
                      [attr.placeholder]="
                        'sku_management.edit.placeholders.sku_code_placeholder'
                          | translate
                      "
                      class="w-full"
                      [class.ng-invalid]="
                        editForm.get('skuCode')?.invalid &&
                        editForm.get('skuCode')?.touched
                      "
                    />
                    @if (
                      editForm.get("skuCode")?.invalid &&
                      editForm.get("skuCode")?.touched
                    ) {
                      <small class="text-red-500">
                        @if (editForm.get("skuCode")?.errors?.["required"]) {
                          {{
                            "sku_management.edit.validation.sku_code_required"
                              | translate
                          }}
                        } @else if (
                          editForm.get("skuCode")?.errors?.["spacesOnly"]
                        ) {
                          SKU Code cannot be empty or contain only spaces
                        } @else if (
                          editForm.get("skuCode")?.errors?.["maxlength"]
                        ) {
                          {{
                            "common.validation.maxlength"
                              | translate
                                : {
                                    fieldName:
                                      "sku_management.detail.fields.sku_code"
                                      | translate,
                                    requiredLength:
                                      editForm.get("skuCode")?.errors?.[
                                        "maxlength"
                                      ]?.requiredLength
                                  }
                          }}
                        } @else if (
                          editForm.get("skuCode")?.errors?.["invalidFormat"]
                        ) {
                          Only letters, numbers, hyphens, and underscores are
                          allowed
                        }
                      </small>
                    }
                  } @else {
                    <p class="text-gray-800">{{ item.skuCode }}</p>
                  }
                </div>

                <div class="space-y-2">
                  <span class="text-sm font-medium text-gray-600">{{
                    "sku_management.detail.fields.product_name" | translate
                  }}</span>
                  <p class="text-gray-800">{{ item.productName }}</p>
                </div>

                <div class="space-y-2">
                  <span class="text-sm font-medium text-gray-600">{{
                    "sku_management.detail.fields.product_category" | translate
                  }}</span>
                  <p class="text-gray-800">{{ item.productCategory }}</p>
                </div>

                <div class="space-y-2">
                  <span class="text-sm font-medium text-gray-600">{{
                    "sku_management.detail.fields.product_code" | translate
                  }}</span>
                  <p class="text-gray-800">{{ item.productCode }}</p>
                </div>

                @if (item.skuType !== "package") {
                  <div class="space-y-2">
                    <span class="text-sm font-medium text-gray-600">{{
                      "sku_management.detail.fields.product_brand" | translate
                    }}</span>
                    <p class="text-gray-800">
                      {{
                        item.productBrand ||
                          ("sku_management.detail.values.na" | translate)
                      }}
                    </p>
                  </div>
                }
              </div>
            </ng-template>
          </p-card>

          <p-card styleClass="border-0 overflow-hidden">
            <ng-template pTemplate="header">
              <div class="flex items-center">
                <h2 class="text-lg font-medium text-gray-800">
                  {{ "goods.tax.section_title" | translate }}
                </h2>
              </div>
            </ng-template>
            <ng-template pTemplate="content">
              <app-product-tax-control
                [value]="taxValue()"
                [inheritedValue]="inheritedTaxValue()"
                [taxOptions]="taxOptions()"
                [idPrefix]="'sku-edit-tax'"
                [disabled]="loading()"
                (valueChange)="onTaxValueChange($event)"
              ></app-product-tax-control>
            </ng-template>
          </p-card>

          <!-- SKU Attributes Section -->
          <p-card styleClass="border-0 overflow-hidden">
            <ng-template pTemplate="header">
              <div class="flex items-center">
                <h2 class="text-lg font-medium text-gray-800">
                  {{
                    "sku_management.detail.sections.sku_attributes" | translate
                  }}
                </h2>
              </div>
            </ng-template>
            <ng-template pTemplate="content">
              <div class="space-y-6">
                @if (
                  item.skuType === "package" &&
                  packageSpuAttributes().length > 0
                ) {
                  <div class="bg-gray-50 rounded-lg p-4 shadow-sm">
                    <h3 class="text-lg font-semibold text-gray-800 mb-4">
                      {{
                        "sku_management.detail.attributes.product_attributes"
                          | translate
                      }}
                    </h3>
                    <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
                      @for (
                        attribute of packageSpuAttributes();
                        track attribute.name
                      ) {
                        <div class="space-y-2">
                          <span class="text-sm font-medium text-gray-600">
                            {{ attribute.name }}
                          </span>
                          <p class="text-gray-800">{{ attribute.value }}</p>
                        </div>
                      }
                    </div>
                  </div>
                }

                <!-- System Attributes (Always Editable) -->
                <div class="bg-gray-50 rounded-lg p-4 shadow-sm">
                  <div class="flex items-center mb-4">
                    <h3 class="text-lg font-semibold text-gray-800">
                      {{
                        "sku_management.detail.attributes.system_attributes"
                          | translate
                      }}
                    </h3>
                  </div>
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div class="space-y-2">
                      <label
                        for="sku-weight"
                        class="text-sm font-medium text-gray-600"
                        >{{
                          "sku_management.detail.fields.product_weight"
                            | translate
                        }}</label
                      >
                      <div
                        class="unit-input-group flex border border-gray-300 rounded-md overflow-hidden"
                        [class.input-group-invalid]="
                          editForm.get('weight')?.invalid &&
                          editForm.get('weight')?.touched
                        "
                      >
                        <p-inputNumber
                          [formControlName]="'weight'"
                          [min]="0"
                          mode="decimal"
                          [maxFractionDigits]="dimensionFractionDigits"
                          [useGrouping]="false"
                          inputId="sku-weight"
                          class="flex-1 border-0 focus:ring-0 focus:border-0"
                          styleClass="w-full border-0"
                        />
                        <div class="unit-postfix">g</div>
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label
                        for="sku-length"
                        class="text-sm font-medium text-gray-600"
                        >{{
                          "sku_management.detail.fields.product_length"
                            | translate
                        }}</label
                      >
                      <div
                        class="unit-input-group flex border border-gray-300 rounded-md overflow-hidden"
                        [class.input-group-invalid]="
                          editForm.get('length')?.invalid &&
                          editForm.get('length')?.touched
                        "
                      >
                        <p-inputNumber
                          [formControlName]="'length'"
                          [min]="0"
                          mode="decimal"
                          [maxFractionDigits]="dimensionFractionDigits"
                          [useGrouping]="false"
                          inputId="sku-length"
                          class="flex-1 border-0 focus:ring-0 focus:border-0"
                          styleClass="w-full border-0"
                        />
                        <div class="unit-postfix">cm</div>
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label
                        for="sku-width"
                        class="text-sm font-medium text-gray-600"
                        >{{
                          "sku_management.detail.fields.product_width"
                            | translate
                        }}</label
                      >
                      <div
                        class="unit-input-group flex border border-gray-300 rounded-md overflow-hidden"
                        [class.input-group-invalid]="
                          editForm.get('width')?.invalid &&
                          editForm.get('width')?.touched
                        "
                      >
                        <p-inputNumber
                          [formControlName]="'width'"
                          [min]="0"
                          mode="decimal"
                          [maxFractionDigits]="dimensionFractionDigits"
                          [useGrouping]="false"
                          inputId="sku-width"
                          class="flex-1 border-0 focus:ring-0 focus:border-0"
                          styleClass="w-full border-0"
                        />
                        <div class="unit-postfix">cm</div>
                      </div>
                    </div>

                    <div class="space-y-2">
                      <label
                        for="sku-height"
                        class="text-sm font-medium text-gray-600"
                        >{{
                          "sku_management.detail.fields.product_height"
                            | translate
                        }}</label
                      >
                      <div
                        class="unit-input-group flex border border-gray-300 rounded-md overflow-hidden"
                        [class.input-group-invalid]="
                          editForm.get('height')?.invalid &&
                          editForm.get('height')?.touched
                        "
                      >
                        <p-inputNumber
                          [formControlName]="'height'"
                          [min]="0"
                          mode="decimal"
                          [maxFractionDigits]="dimensionFractionDigits"
                          [useGrouping]="false"
                          inputId="sku-height"
                          class="flex-1 border-0 focus:ring-0 focus:border-0"
                          styleClass="w-full border-0"
                        />
                        <div class="unit-postfix">cm</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Dynamic Attributes -->
                @for (
                  attributeType of getDynamicAttributeTypes();
                  track attributeType
                ) {
                  @if (hasAttributesForType(attributeType)) {
                    <div class="bg-gray-50 rounded-lg p-4 shadow-sm">
                      <div class="flex items-center mb-4">
                        <h3 class="text-lg font-semibold text-gray-800">
                          {{
                            getAttributeSectionLabel(attributeType) | translate
                          }}
                        </h3>
                      </div>
                      <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
                        @for (
                          attribute of getAttributesByType(attributeType);
                          track attribute.id
                        ) {
                          <div class="space-y-2">
                            <span class="text-sm font-medium text-gray-600">
                              @if (attribute.isSku) {
                                <span class="text-red-500 mr-1">#</span>
                              }
                              {{ attribute.attributeName }}
                            </span>
                            @if (isAttributeEditable(attributeType)) {
                              <input
                                pInputText
                                [formControlName]="'attribute_' + attribute.id"
                                class="w-full"
                                [class.ng-invalid]="
                                  editForm.get('attribute_' + attribute.id)
                                    ?.invalid &&
                                  editForm.get('attribute_' + attribute.id)
                                    ?.touched
                                "
                              />
                            } @else {
                              <p class="text-gray-800">
                                {{
                                  attribute.attributeValue ||
                                    ("sku_management.detail.values.na"
                                      | translate)
                                }}
                              </p>
                            }
                          </div>
                        }
                      </div>
                    </div>
                  }
                }

                @if (item.skuType === "package") {
                  <div class="bg-white rounded-lg p-4 shadow-sm">
                    <div class="flex items-center justify-between gap-4 mb-4">
                      <h3 class="text-lg font-semibold text-gray-800">
                        {{
                          "sku_management.create.package_components.title"
                            | translate
                        }}
                      </h3>
                      <app-button
                        [label]="
                          'sku_management.create.delete_selected' | translate
                        "
                        type="danger"
                        size="small"
                        [outlined]="true"
                        [disabled]="
                          selectedPackageComponents().length === 0
                        "
                        (emitOnClick)="removeSelectedPackageComponents()"
                      ></app-button>
                    </div>

                    <div class="overflow-x-auto">
                      <table class="package-components-table w-full">
                        <thead>
                          <tr>
                            <th class="package-component-selection-column">
                              <p-checkbox
                                [binary]="true"
                                [ngModel]="isAllPackageComponentsSelected()"
                                (onChange)="
                                  onPackageComponentSelectAll($event)
                                "
                              ></p-checkbox>
                            </th>
                            <th class="package-component-code-column">
                              {{
                                "sku_management.create.package_components.sku_code"
                                  | translate
                              }}
                            </th>
                            <th class="package-component-name-column">
                              {{
                                "sku_management.create.package_components.sku_name"
                                  | translate
                              }}
                            </th>
                            <th class="package-component-quantity-column">
                              {{
                                "sku_management.create.package_components.qty"
                                  | translate
                              }}
                            </th>
                            <th class="package-component-action-column">
                              {{
                                "sku_management.create.package_components.action"
                                  | translate
                              }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (
                            component of packageComponents();
                            track component.packageSkuId
                          ) {
                            <tr>
                              <td class="text-center">
                                <p-checkbox
                                  [binary]="true"
                                  [ngModel]="
                                    isPackageComponentSelected(component)
                                  "
                                  (onChange)="
                                    onPackageComponentSelectionChange(
                                      $event,
                                      component
                                    )
                                  "
                                ></p-checkbox>
                              </td>
                              <td>{{ component.skuCode }}</td>
                              <td>{{ component.skuName }}</td>
                              <td class="package-component-quantity-cell">
                                <p-inputNumber
                                  [(ngModel)]="component.num"
                                  [ngModelOptions]="{ standalone: true }"
                                  [min]="1"
                                  [showButtons]="true"
                                  buttonLayout="horizontal"
                                  spinnerMode="horizontal"
                                  decrementButtonIcon="pi pi-minus"
                                  incrementButtonIcon="pi pi-plus"
                                  [useGrouping]="true"
                                  [minFractionDigits]="0"
                                  [maxFractionDigits]="0"
                                  [inputStyle]="{
                                    textAlign: 'center',
                                    minWidth: '3rem'
                                  }"
                                  (onInput)="
                                    onPackageComponentQuantityChange(component)
                                  "
                                  styleClass="w-full package-component-quantity-input"
                                />
                              </td>
                              <td class="text-center">
                                <button
                                  type="button"
                                  class="action-link action-delete"
                                  (click)="removePackageComponent(component)"
                                >
                                  {{
                                    "sku_management.create.package_components.remove"
                                      | translate
                                  }}
                                </button>
                              </td>
                            </tr>
                          } @empty {
                            <tr>
                              <td colspan="5" class="text-center text-gray-500">
                                {{
                                  "sku_management.create.package_components.no_child_sku_selected"
                                    | translate
                                }}
                              </td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  </div>
                }

                <!-- Barcode Table Section -->
                <div class="bg-white rounded-lg p-4 shadow-sm">
                  <div class="flex items-center mb-4">
                    <h3 class="text-lg font-semibold text-gray-800">
                      {{
                        "sku_management.detail.sections.identification_attributes"
                          | translate
                      }}
                    </h3>
                  </div>
                  <app-barcode-table
                    [barcodeList]="barcodeList()"
                    [selectedBarcodes]="selectedBarcodes()"
                    [showTitle]="true"
                    (addBarcode)="onAddBarcode()"
                    (editBarcode)="onEditBarcode($event)"
                    (deleteBarcode)="onDeleteBarcode($event)"
                    (deleteSelectedBarcodes)="onDeleteSelectedBarcodes()"
                    (selectionChange)="onBarcodeSelectionChange($event)"
                  ></app-barcode-table>
                </div>
              </div>
            </ng-template>
          </p-card>

        </form>
      }
    }
  </div>
</div>

<!-- Barcode Dialog -->
<app-barcode-dialog
  [(visible)]="showBarcodeDialog"
  [initialData]="editingBarcode()"
  (confirm)="onBarcodeDialogConfirm($event)"
></app-barcode-dialog>
